import React, { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Dialogger, Toaster, Button, Icons } from '@densityco/ui';
import HorizontalForm from '../../HorizontalForm';
import MoreButton from '../../MoreButton';
import DisabledButtonTooltip from '../../DisabledButtonTooltip';

import CaptureScheduleModal from '../../CaptureScheduleModal';
import MultiPointInstallationCreationModal from '../../MultiPointInstallation/Creation';

import TriggerDataCapture from './TriggerDataCapture';
import StopDataCapture from './StopDataCapture';
import SensorRecalibrate from './SensorRecalibrate';
import MeasureHeight from './MeasureHeight';

import { Sensor, SensorType } from '../../../types/api';
import { ON_PREM } from '../../../utils/environment';
import { SenseiApi } from '../../../utils/api';
import { useRequiredSessionToken } from '../../../utils/session-token';
import { commaSeparateList } from '../../../utils/comma-separate';
import { FIRST_BUILD_WITH_SENSOR_LOCATE_AND_IMMEDIATE_COMMANDS, supportsLocate } from '../../../utils/sensor';

type SensorActionButtonsProps = {
  sensors: Array<{
    serial_number: Sensor['serial_number'];
    link_id: Sensor['link_id'];
    doorway_name: Sensor['doorway_name'];
    organization: Sensor['organization'];
    sensor_type: Sensor['sensor_type'];
    os_version: Sensor['os_version'];
    diagnostic_info_json: Sensor['diagnostic_info_json'];
    in_mpi?: Sensor['in_mpi'];
  }>;

  // Is this component being rendered on a list page, or on a detail page?
  presentation: 'list' | 'detail';

  // Render an application-specific primary action button for the view
  primaryActionButton?: React.ReactNode;

  showMPICreationButton?: boolean;
  showMeasureHeightButton?: boolean;

  // Called when an action is performed and the representation for a sensor should be re-fetched
  // from the server
  onReloadSensors?: (sensorSerialNumbers: Array<string>) => void;
};

const SensorActionButtons: React.FunctionComponent<SensorActionButtonsProps> = ({
  sensors,
  presentation='list',
  primaryActionButton=null,
  showMPICreationButton=false,
  showMeasureHeightButton=false,
  onReloadSensors=() => {},
}) => {
  const accessToken = useRequiredSessionToken();
  const { organizationIdOrAll } = useParams();

  const [captureScheduleModalOpen, setCaptureScheduleModalOpen] = useState(
    false,
  );
  const [mpiModalOpen, setMpiModalOpen] = useState(false);

  const serialNumbers = sensors.map(i => i.serial_number);

  const locateButton = sensors.length === 1 ? (
    supportsLocate(sensors[0]) ? (
      <Button
        onClick={() => {
          SenseiApi.locate(accessToken, sensors[0].serial_number).then(() => {
            Toaster.showToast({
              text: 'Locate command sent!',
            });
          }).catch(error => {
            Toaster.showToast({ type: 'error', text: error.message});
          })
        }}
        leftIcon={<Icons.AccuracyTarget />}
      >
        Locate
      </Button>
    ) : (
      <DisabledButtonTooltip
        contents={`The locate command only works on sensors running an os version ${FIRST_BUILD_WITH_SENSOR_LOCATE_AND_IMMEDIATE_COMMANDS} or higher.`}
        target={<Button disabled leftIcon={<Icons.AccuracyTarget />}>Locate</Button>}
      />
    )
  ) : null;

  // Only show "configure sensor" when an oa unit is selected
  if (presentation === "detail" && sensors.length > 0 && sensors[0].sensor_type === SensorType.OB1) {
    return (
      <HorizontalForm>
        {locateButton}
        <Button
          variant="filled"
          href={`#/${organizationIdOrAll}/sensors/${sensors[0].serial_number}/configure`}
          leftIcon={<Icons.Lab />}
        >
          Configure Sensor
        </Button>
      </HorizontalForm>
    );
  }

  const issueRequest = async (
    operationName: string,
    operationPhrase: string,
    senseiApiFunction: (accessToken: string, serialNumbers: Array<Sensor['serial_number']>) => Promise<void>,
  ): Promise<void> => {
    const serialNumberList = commaSeparateList(sensors.map(i => i.serial_number));

    return Dialogger.confirm({
      title: operationName,
      prompt: (
        <p>
          Are you sure you would like to {operationPhrase} {serialNumberList}?
        </p>
      ),
      confirmText: operationName,
    }).then(ok => {
      if (!ok) {
        return;
      }

      senseiApiFunction(accessToken, sensors.map(i => i.serial_number)).then(() => {
        Toaster.showToast({
          text: `${operationName} command sent to ${sensors.length} ${sensors.length === 1 ? 'sensor' : 'sensors'}.`,
        });
        onReloadSensors(serialNumbers);
      }).catch(error => {
        Toaster.showToast({ type: 'error', text: error.message});
      })
    });
  };

  return (
    <Fragment>
      <CaptureScheduleModal
        open={captureScheduleModalOpen}
        onDismiss={() => setCaptureScheduleModalOpen(false)}
        sensors={sensors.map(sensor => ({
          serial_number: sensor.serial_number,
          link_id: sensor.link_id,
          name: sensor.doorway_name,
          organization: {
            id: sensor.organization.id,
            name: sensor.organization.name,
          },
          sensor_type: sensor.sensor_type,
        }))}
      />

      <MultiPointInstallationCreationModal
        open={mpiModalOpen}
        onDismiss={() => setMpiModalOpen(false)}
        sensors={sensors.map(item => ({
          serialNumber: item.serial_number,
          doorwayName: item.doorway_name,
        }))}
      />

      <HorizontalForm>
        {showMPICreationButton ? (
          <Button
            onClick={() => setMpiModalOpen(true)}
            data-test-id="in-configuration-actions-create-mpi"
          >
            Create MPI...
          </Button>
        ) : null}

        <SensorRecalibrate sensors={sensors} onComplete={() => onReloadSensors(serialNumbers)} />

        <TriggerDataCapture sensors={sensors} onComplete={() => onReloadSensors(serialNumbers)} />
        {showMeasureHeightButton ? (
          <MeasureHeight sensor={sensors[0]} />
        ) : null}

        {/* Show "schedule captures" on the bar on list views, or in contexts where sensors are bulk selected */}
        {!ON_PREM && (presentation === "list" || (presentation === 'detail' && sensors.length > 1)) ? (
          <Button
            variant={primaryActionButton ? undefined : 'filled'}
            onClick={() => setCaptureScheduleModalOpen(true)}
          >
            Schedule Captures
          </Button>
        ) : null}

        {/* Only permit locating sensors when on a detail page where only one sensor is selected */}
        {presentation === 'detail' && sensors.length === 1 ? locateButton : null}

        {primaryActionButton}

        <MoreButton
          choices={[
            // Show "schedule captures" in the more menu if on the detail page
            ...(!ON_PREM && presentation === "detail" ? [{
              id: 'schedule-captures',
              label: "Schedule Captures",
              onClick: () => setCaptureScheduleModalOpen(true),
            }] : []),

            {
              id: 'stop-data-capture',
              label: "Stop Data Captures",
              onClick: () => issueRequest("Stop Data Captures", "stop any data captures currently in progress at", SenseiApi.stopCapture),
            },
            {
              id: 'reboot',
              label: "Reboot",
              onClick: () => issueRequest("Reboot", "reboot", SenseiApi.reboot),
            },
            {
              id: 'upload-logs',
              label: "Upload Logs",
              onClick: () => issueRequest("Upload Logs", "upload sensor logs from", SenseiApi.uploadLogs),
            },
            {
              id: 'factory-reset',
              label: "Factory Reset",
              onClick: () => issueRequest("Factory Reset", "factory reset", SenseiApi.factoryReset),
            },
            {
              id: 'rma-reset',
              label: "RMA Reset",
              onClick: () => issueRequest("RMA Reset", "RMA reset", SenseiApi.rmaReset),
            },
            {
              id: 'prioritize-annotations',
              label: "Prioritize Annotations (72 hours)",
              onClick: () => {
                const serialNumberList = commaSeparateList(serialNumbers);

                return Dialogger.confirm({
                  title: "Prioritize Annotations",
                  prompt: (
                    <p>
                      Are you sure you would like to prioritize annotations for the next 72 hours for {serialNumberList}?
                    </p>
                  ),
                  confirmText: "Prioritize",
                }).then(ok => {
                  if (!ok) {
                    return;
                  }

                  return SenseiApi.enableSensorPriorityAnnotation(accessToken, serialNumbers);
                }).then(() => {
                  Toaster.showToast({
                    text: `72 hours of priority annotation enabled for ${sensors.length} ${sensors.length === 1 ? 'sensor' : 'sensors'}`,
                  });
                  onReloadSensors(serialNumbers);
                }).catch(error => {
                  Toaster.showToast({ type: 'error', text: 'Error enabling priority annotation!' });
                });
              },
            },
          ]}
        />
      </HorizontalForm>
    </Fragment>
  );
};

export default SensorActionButtons;
